import {
  DEFAULT_WORLD_BALANCE,
  type WorldBalance,
} from "../../models/config/worldBalance.ts";
import type { DomainEvent } from "../../models/events.ts";
import type { RandomEventEngineState } from "../../models/randomEvent.ts";
import type { RngState, WorldState } from "../../models/worldState.ts";
import { nextRngUnit } from "../../models/rng/seededRng.ts";
import { getDefaultRandomEventDefinitions } from "./eventDefinitions.ts";
import { pickRandomEventTile } from "./pickEventTile.ts";
import {
  processTurnEnd,
  type ProcessTurnEndOptions,
  type RandomEventLifecycleNotification,
} from "./processTurnEnd.ts";

function createConsumingRandom(rng: RngState): {
  random: () => number;
  getRng: () => RngState;
} {
  let current = rng;
  return {
    random: () => {
      const next = nextRngUnit(current);
      current = next.rng;
      return next.value;
    },
    getRng: () => current,
  };
}

function lifecycleToDomainEvents(
  notifications: readonly RandomEventLifecycleNotification[],
): DomainEvent[] {
  const events: DomainEvent[] = [];
  for (const notification of notifications) {
    if (notification.type === "eventStarted") {
      events.push({
        type: "RandomEventStarted",
        eventId: notification.eventId,
        startedAtTurn: notification.startedAtTurn,
        tile: notification.tile,
      });
    } else if (notification.type === "eventTileEntered") {
      events.push({
        type: "RandomEventTileEntered",
        eventId: notification.eventId,
        tile: notification.tile,
      });
    } else {
      events.push({
        type: "RandomEventFinished",
        eventId: notification.eventId,
        reason: notification.reason,
      });
    }
  }
  return events;
}

export function applyRandomEventTurnEnd(
  state: WorldState,
  options: ProcessTurnEndOptions = {},
  balance: WorldBalance = DEFAULT_WORLD_BALANCE,
): {
  randomEvent: RandomEventEngineState;
  rng: RngState;
  events: DomainEvent[];
} {
  const { random, getRng } = createConsumingRandom(state.rng);
  const candidates = Object.values(state.tiles).map((tile) => ({
    q: tile.q,
    r: tile.r,
  }));

  const result = processTurnEnd(
    state.randomEvent,
    getDefaultRandomEventDefinitions(balance),
    {
      currentTurn: state.turn,
      clanTile: state.clan.currentTile,
      resolveEventTile: () =>
        pickRandomEventTile(candidates, state.clan.currentTile, random),
    },
    random,
    options,
  );

  return {
    randomEvent: result.state,
    rng: getRng(),
    events: lifecycleToDomainEvents(result.notifications),
  };
}
